<script>
export default {
    name: 'BaseCalendarNavigationPeriodDays',
};
</script>

<script setup>
import { ref, watch } from 'vue';
import formatDate from '@/utils/format-date.ts';
import { areDatesEqual } from '@/components/base-components/components/date-picker/utils/helpers.js';
import BaseCalendarViewMonth from '@/components/base-components/components/calendar/components/view/month.vue';

const props = defineProps({
    value: {
        type: Date,
        required: true,
    },
    firstDayOfWeek: {
        type: Number,
        default: 1,
    },
});

const emit = defineEmits({
    input: null,
});

const date = ref(new Date(props.value));

watch(() => props.value, () => {
    date.value = new Date(props.value);
});

function changeMonth(delta) {
    const current = new Date(date.value);
    const currentDay = current.getDate();

    current.setDate(1);
    current.setMonth(current.getMonth() + delta);

    const lastDayOfMonth = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
    ).getDate();

    current.setDate(Math.min(currentDay, lastDayOfMonth));

    date.value = current;
}

function goToPrevious() {
    changeMonth(-1);
}

function goToNext() {
    changeMonth(1);
}
</script>

<template>
    <div>
        <slot
            name="navigation"
            :label="formatDate(date.toString(), { month: 'short', year: 'numeric' })"
            :go-to-previous="goToPrevious"
            :go-to-next="goToNext"
        />

        <base-calendar-view-month
            :date="date"
            :first-day-of-week="firstDayOfWeek"
        >
            <template #title="{ date }">
                <div
                    class="flex items-center justify-center px-1 py-2 font-medium text-gray-400"
                >
                    {{ formatDate(date.toISOString(), { weekday: 'short' }) }}
                </div>
            </template>

            <template #cell="{ date: item, isToday, isCurrentMonth }">
                <div
                    class="flex aspect-square w-full cursor-pointer items-center justify-center my-0.5 p-0.75 group"
                    @click="emit('input', item)"
                >
                    <div
                        class="relative flex h-full w-full select-none items-center justify-center rounded-full font-medium"
                        :class="[
                            {
                                'text-gray-300': !isCurrentMonth,
                                'font-semibold': isToday,
                            },
                            areDatesEqual(item, value) ? 'text-white bg-secondary-950 hover:bg-secondary-800' : 'hover:bg-science-blue-800/10 group-hover:bg-science-blue-800/10',
                        ]"
                    >
                        {{ item.getDate() }}

                        <div
                            v-if="isToday"
                            class="absolute mt-4 w-1/2 border-t-2 border-current"
                        />
                    </div>
                </div>
            </template>
        </base-calendar-view-month>
    </div>
</template>
